'use client'

import { DateInput } from '@/components/molecules/DateInput'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useStation } from '@/hooks/useStation'
import { useStations } from '@/hooks/useStations'
import {
	formatStationIntervalsLabel,
	getSlotGlobalStatus,
	getSlotStationStatus,
} from '@/lib/station-open-hours'
import { useReservationStore } from '@/store'
import { SearchRequestModel, StationModel } from '@/types/api-models'
import { format, isBefore, startOfDay } from 'date-fns'
import { CalendarIcon, MapPin } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

interface SearchWidgetProps {
	onSearch?: () => void
	className?: string
	redirectUrl?: string
}

const HALF_HOUR_SLOTS = Array.from({ length: 48 }, (_, index) => {
	const hours = String(Math.floor(index / 2)).padStart(2, '0')
	const minutes = index % 2 === 0 ? '00' : '30'

	return `${hours}:${minutes}`
})

function normalizeTimeSlot(time: string) {
	const [rawHours = '10', rawMinutes = '00'] = time.split(':')
	const hours = Number(rawHours)
	const minutes = Number(rawMinutes)

	if (Number.isNaN(hours) || Number.isNaN(minutes)) {
		return '10:00'
	}

	const totalMinutes = hours * 60 + minutes
	const roundedMinutes = Math.round(totalMinutes / 30) * 30
	const clampedMinutes = Math.min(23 * 60 + 30, Math.max(0, roundedMinutes))

	return `${String(Math.floor(clampedMinutes / 60)).padStart(2, '0')}:${String(
		clampedMinutes % 60,
	).padStart(2, '0')}`
}

function normalizeIsoDateTime(iso: string) {
	try {
		const date = new Date(iso)
		if (Number.isNaN(date.getTime())) return iso

		return `${format(date, 'yyyy-MM-dd')}T${normalizeTimeSlot(
			format(date, 'HH:mm'),
		)}`
	} catch {
		return iso
	}
}

function parseDateFromSearchIso(iso: string): Date | undefined {
	try {
		const date = new Date(iso)
		if (Number.isNaN(date.getTime())) return undefined
		return date
	} catch {
		return undefined
	}
}

function parseTimeFromSearchIso(iso: string): string {
	try {
		const date = new Date(iso)
		if (Number.isNaN(date.getTime())) return '10:00'
		return normalizeTimeSlot(format(date, 'HH:mm'))
	} catch {
		return '10:00'
	}
}

function normalizeSearchParamsForSubmit(
	params: SearchRequestModel,
): SearchRequestModel {
	return {
		...params,
		pickupDate: normalizeIsoDateTime(params.pickupDate),
		dropoffDate: normalizeIsoDateTime(params.dropoffDate),
	}
}

function isSlotSelectable(
	station: StationModel | undefined,
	date: Date | undefined,
	slot: string,
) {
	const ss = getSlotStationStatus(station, date, slot)
	if (ss !== null) return ss !== 'closed'
	return getSlotGlobalStatus(slot) !== 'closed'
}

export function SearchWidget({ onSearch, className, redirectUrl }: SearchWidgetProps) {
	const { searchParams, setSearchParams } = useReservationStore()
	const { stations, isLoading: isLoadingStations } = useStations()
	const normalizedInitialParams = useMemo(
		() => normalizeSearchParamsForSubmit(searchParams),
		[searchParams],
	)

	const [localParams, setLocalParams] = useState<SearchRequestModel>(
		normalizedInitialParams,
	)
	const [sameLocation, setSameLocation] = useState(
		searchParams.pickupStationId === searchParams.dropoffStationId,
	)

	const pickupStationBase = useMemo(
		() =>
			stations.find(
				s => String(s.id) === String(localParams.pickupStationId),
			),
		[stations, localParams.pickupStationId],
	)
	const dropoffStationBase = useMemo(
		() =>
			stations.find(
				s => String(s.id) === String(localParams.dropoffStationId),
			),
		[stations, localParams.dropoffStationId],
	)

	const { station: pickupStationDetail } = useStation(localParams.pickupStationId || undefined)
	const { station: dropoffStationDetail } = useStation(
		!sameLocation ? localParams.dropoffStationId || undefined : undefined,
	)

	// Merge list data with per-station detail; detail takes precedence for hours
	const pickupStation = useMemo(
		() =>
			pickupStationDetail
				? { ...pickupStationBase, ...pickupStationDetail }
				: pickupStationBase,
		[pickupStationBase, pickupStationDetail],
	)
	const dropoffStation = useMemo(
		() =>
			dropoffStationDetail
				? { ...dropoffStationBase, ...dropoffStationDetail }
				: sameLocation
					? pickupStation
					: dropoffStationBase,
		[dropoffStationBase, dropoffStationDetail, sameLocation, pickupStation],
	)

	const pickupCalendarDate = useMemo(
		() => parseDateFromSearchIso(localParams.pickupDate),
		[localParams.pickupDate],
	)
	const dropoffCalendarDate = useMemo(
		() => parseDateFromSearchIso(localParams.dropoffDate),
		[localParams.dropoffDate],
	)

	const pickupTimeOptions = useMemo(
		() =>
			HALF_HOUR_SLOTS.map(value => {
				const ss = getSlotStationStatus(pickupStation, pickupCalendarDate, value)
				if (ss === 'normal') return { value, disabled: false, label: `${value} - Abierto` }
				if (ss === 'after-hours') return { value, disabled: false, label: `${value} - Abierto con recargo` }
				if (ss === 'closed') return { value, disabled: true, label: `${value} - Cerrado` }
				const gs = getSlotGlobalStatus(value)
				if (gs === 'closed') return { value, disabled: true, label: `${value} - Cerrado` }
				if (gs === 'after-hours') return { value, disabled: false, label: `${value} - Abierto con recargo` }
				return { value, disabled: false, label: `${value} - Abierto` }
			}),
		[pickupStation, pickupCalendarDate],
	)

	const dropoffTimeOptions = useMemo(
		() =>
			HALF_HOUR_SLOTS.map(value => {
				const ss = getSlotStationStatus(dropoffStation, dropoffCalendarDate, value)
				if (ss === 'normal') return { value, disabled: false, label: `${value} - Abierto` }
				if (ss === 'after-hours') return { value, disabled: false, label: `${value} - Abierto con recargo` }
				if (ss === 'closed') return { value, disabled: true, label: `${value} - Cerrado` }
				const gs = getSlotGlobalStatus(value)
				if (gs === 'closed') return { value, disabled: true, label: `${value} - Cerrado` }
				if (gs === 'after-hours') return { value, disabled: false, label: `${value} - Abierto con recargo` }
				return { value, disabled: false, label: `${value} - Abierto` }
			}),
		[dropoffStation, dropoffCalendarDate],
	)

	// Sync local state from store only on initial mount.
	// The component unmounts/remounts each time the dialog opens/closes,
	// so useState above already initializes with the current store values.
	// Using empty deps prevents this effect from overriding the user's
	// in-progress changes if the store updates while the modal is open.

	useEffect(() => {
		setLocalParams(normalizeSearchParamsForSubmit(searchParams))
		setSameLocation(
			searchParams.pickupStationId === searchParams.dropoffStationId,
		)
	}, [])

	/** Si el tiempo seleccionado cae fuera del horario válido (estación o, en su ausencia, regla global), saltar al primero válido. */
	useEffect(() => {
		setLocalParams(prev => {
			const d = parseDateFromSearchIso(prev.pickupDate)
			if (!d) return prev
			const t = parseTimeFromSearchIso(prev.pickupDate)
			if (isSlotSelectable(pickupStation, d, t)) return prev
			const first = HALF_HOUR_SLOTS.find(s => isSlotSelectable(pickupStation, d, s))
			if (!first) return prev
			return { ...prev, pickupDate: `${format(d, 'yyyy-MM-dd')}T${first}` }
		})
	}, [localParams.pickupDate, pickupStation])

	useEffect(() => {
		setLocalParams(prev => {
			const d = parseDateFromSearchIso(prev.dropoffDate)
			if (!d) return prev
			const t = parseTimeFromSearchIso(prev.dropoffDate)
			if (isSlotSelectable(dropoffStation, d, t)) return prev
			const first = HALF_HOUR_SLOTS.find(s => isSlotSelectable(dropoffStation, d, s))
			if (!first) return prev
			return { ...prev, dropoffDate: `${format(d, 'yyyy-MM-dd')}T${first}` }
		})
	}, [localParams.dropoffDate, dropoffStation])

	const handleSearch = () => {
		const normalizedParams = normalizeSearchParamsForSubmit(localParams)
		setLocalParams(normalizedParams)

		if (redirectUrl) {
			const params = new URLSearchParams({
				pickupStationId: normalizedParams.pickupStationId,
				dropoffStationId: normalizedParams.dropoffStationId,
				pickupDate: normalizedParams.pickupDate,
				dropoffDate: normalizedParams.dropoffDate,
				driverAge: String(normalizedParams.driverAge),
				countryCode: normalizedParams.countryCode,
			})
			if (normalizedParams.promotionalCode) {
				params.set('promotionalCode', normalizedParams.promotionalCode)
			}
			;(window.top ?? window).location.href = `${redirectUrl}?${params.toString()}`
			return
		}

		setSearchParams(normalizedParams)
		onSearch?.()
	}

	const updateParam = (
		key: keyof SearchRequestModel,
		value: string | number,
	) => {
		setLocalParams(prev => {
			const newParams = { ...prev, [key]: value }
			if (key === 'pickupStationId' && sameLocation) {
				newParams.dropoffStationId = value as string
			}
			return newParams
		})
	}

	const toggleSameLocation = (checked: boolean) => {
		setSameLocation(checked)
		if (checked) {
			updateParam('dropoffStationId', localParams.pickupStationId)
		}
	}

	const getDateFromISO = (iso: string) => parseDateFromSearchIso(iso)

	const getTimeFromISO = (iso: string) => parseTimeFromSearchIso(iso)

	const updateDate = (
		key: 'pickupDate' | 'dropoffDate',
		date: Date | undefined,
	) => {
		if (!date) return
		const currentISO = localParams[key]
		const time = getTimeFromISO(currentISO)
		const newISO = format(date, 'yyyy-MM-dd') + 'T' + time
		updateParam(key, newISO)
	}

	const updateTime = (key: 'pickupDate' | 'dropoffDate', time: string) => {
		const currentISO = localParams[key]
		const date = getDateFromISO(currentISO)
		if (!date) return
		const newISO = format(date, 'yyyy-MM-dd') + 'T' + time
		updateParam(key, newISO)
	}

	return (
		<div className={`bg-white p-6 rounded-lg shadow-lg ${className}`}>
			<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
				{/* Pickup Location */}
				<div className='space-y-2'>
					<Label className='flex items-center gap-2 text-[#09194D] font-bold'>
						<MapPin className='w-4 h-4' />
						Oficina de Retiro
					</Label>
					<Select
						value={localParams.pickupStationId}
						onValueChange={val => updateParam('pickupStationId', val)}
						disabled={isLoadingStations}
					>
						<SelectTrigger className='w-full border-[#09194D] h-13'>
							<SelectValue placeholder='Seleccionar oficina' />
						</SelectTrigger>
						<SelectContent>
							{stations.map(station => (
								<SelectItem
									key={station.id}
									value={String(station.id)}
								>
									{station.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Return Location */}
				<div className='space-y-2'>
					<div className='flex items-center justify-between'>
						<Label className='flex items-center gap-2 text-[#09194D] font-bold'>
							<MapPin className='w-4 h-4' />
							Oficina de Devolución
						</Label>
						<div className='flex items-center gap-2'>
							<Checkbox
								id='same-location'
								checked={sameLocation}
								onCheckedChange={toggleSameLocation}
								className='border-[#09194D] data-[state=checked]:bg-[#09194D]'
							/>
							<Label
								htmlFor='same-location'
								className='text-xs font-normal cursor-pointer'
							>
								Misma oficina
							</Label>
						</div>
					</div>
					<Select
						value={localParams.dropoffStationId}
						onValueChange={val => updateParam('dropoffStationId', val)}
						disabled={isLoadingStations || sameLocation}
					>
						<SelectTrigger className='w-full border-[#09194D] h-13'>
							<SelectValue placeholder='Seleccionar oficina' />
						</SelectTrigger>
						<SelectContent>
							{stations.map(station => (
								<SelectItem
									key={station.id}
									value={String(station.id)}
								>
									{station.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Pickup Date */}
				<div className='space-y-2'>
					<Label className='flex items-center gap-2 text-[#09194D] font-bold'>
						<CalendarIcon className='w-4 h-4' />
						Fecha de Retiro
					</Label>
					<div className='flex gap-2'>
						<DateInput
							value={getDateFromISO(localParams.pickupDate)}
							onChange={date => updateDate('pickupDate', date)}
							disabledDates={date => isBefore(date, startOfDay(new Date()))}
							className='flex-1'
						/>
						<Select
							value={getTimeFromISO(localParams.pickupDate)}
							onValueChange={value => updateTime('pickupDate', value)}
						>
							<SelectTrigger className='w-40 border-[#09194D]'>
								<SelectValue placeholder='Hora' />
							</SelectTrigger>
							<SelectContent>
								{pickupTimeOptions.map(option => (
									<SelectItem
										key={`pickup-${option.value}`}
										value={option.value}
										disabled={option.disabled}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{/* Return Date */}
				<div className='space-y-2'>
					<Label className='flex items-center gap-2 text-[#09194D] font-bold'>
						<CalendarIcon className='w-4 h-4' />
						Fecha de Devolución
					</Label>
					<div className='flex gap-2'>
						<DateInput
							value={getDateFromISO(localParams.dropoffDate)}
							onChange={date => updateDate('dropoffDate', date)}
							disabledDates={date => {
								const pickupDate = getDateFromISO(localParams.pickupDate)
								if (!pickupDate) return true
								return (
									isBefore(date, startOfDay(new Date())) ||
									isBefore(date, pickupDate)
								)
							}}
							className='flex-1'
						/>
						<Select
							value={getTimeFromISO(localParams.dropoffDate)}
							onValueChange={value => updateTime('dropoffDate', value)}
						>
							<SelectTrigger className='w-40 border-[#09194D]'>
								<SelectValue placeholder='Hora' />
							</SelectTrigger>
							<SelectContent>
								{dropoffTimeOptions.map(option => (
									<SelectItem
										key={`dropoff-${option.value}`}
										value={option.value}
										disabled={option.disabled}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				{(() => {
					const labelPickup = formatStationIntervalsLabel(
						pickupStation,
						pickupCalendarDate,
					)
					const labelDropoff = formatStationIntervalsLabel(
						dropoffStation,
						dropoffCalendarDate,
					)
					if (!labelPickup && !labelDropoff) return null
					return (
						<p className='md:col-span-2 text-xs text-[#09194D]/70'>
							{labelPickup && (
								<>
									<span className='font-semibold'>Retiro (fecha elegida): </span>
									{labelPickup}.{' '}
								</>
							)}
							{labelDropoff && (
								<>
									<span className='font-semibold'>
										Devolución (fecha elegida):{' '}
									</span>
									{labelDropoff}.{' '}
								</>
							)}
							Abierto: horario regular de la oficina · Abierto con recargo: fuera del horario regular pero dentro del rango operativo (con cargo adicional) · Cerrado: oficina cerrada (no seleccionable).
						</p>
					)
				})()}
			</div>

			<div className='mt-6 flex justify-end'>
				<Button
					onClick={handleSearch}
					className='bg-[#CDFF00] hover:bg-[#b0db00] text-[#09194D] font-["Bricolage_Grotesque"] font-black text-lg px-8 py-6 rounded-full cursor-pointer'
				>
					BUSCAR VEHÍCULO
				</Button>
			</div>
		</div>
	)
}
